import { Box, Skeleton, useTheme } from '@mui/material';
import { type GridColDef, type GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DatagridEmailExportToolbar } from './DatagridEmailExportToolbar';
import { DATAGRID_PAGE_SIZE_OPTIONS } from '../../../../common/constants';
import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { DataGridLayout } from '../../../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../../../wrapper-components/DataGrid/DataGrid';
import { useMetricSummayDatagridFetch } from '../../hook/useMetricSummaryDataGridFetch';
import { setSummaryTablePaginationModel } from '../../state/metricsSlice';
import { selectSummaryTablePage, selectSummaryTablePageSize } from '../../state/selector';
import { MetricsSummaryItem } from '../../types/responseTypes';

const nullToZero = (value: unknown) => (value == null ? 0 : value);

export const MetricSummaryTable = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const summaryTablePage = useAppSelector(selectSummaryTablePage);
  const summaryTablePageSize = useAppSelector(selectSummaryTablePageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setSummaryTablePaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const { isFetching } = useMetricSummayDatagridFetch() || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(setSummaryTablePaginationModel({ page: 0, pageSize: summaryTablePageSize }));
    }
  }, [isFetching]);

  const columns: GridColDef<MetricsSummaryItem>[] = useMemo(
    () => [
      {
        field: 'srNo',
        headerName: 'Sr No.',
        flex: 0.3,
        minWidth: 100,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value: unknown, row: MetricsSummaryItem, _column, apiRef) =>
          apiRef.current.getRowIndexRelativeToVisibleRows(row.id) + 1,
      },
      {
        field: 'projectName',
        headerName: 'Project name',
        flex: 1.2,
        minWidth: 250,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
            title={params.value} // 👈 tooltip on hover (important)
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'cohort',
        headerName: 'Cohort',
        flex: 1,
        minWidth: 120,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'resolution',
        headerName: 'Resolution',
        flex: 1,
        minWidth: 120,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'reportDate',
        headerName: 'Report date',
        flex: 0.8,
        minWidth: 140,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
      },
      {
        field: 'startDate',
        headerName: 'Start date',
        flex: 0.8,
        minWidth: 140,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
      },
      {
        field: 'projectedDeliveryDate',
        headerName: 'Project delivery date',
        flex: 0.8,
        minWidth: 200,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
      },
      {
        field: 'encountersRequired',
        headerName: 'Encounters required',
        flex: 0.6,
        minWidth: 180,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'encountersCompletedOverLastDay',
        headerName: 'Encounters completed over last day',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'encountersCompletedTotal',
        headerName: 'Total encounters completed',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'percentComplete',
        headerName: 'Percent completed',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => `${(value ?? 0).toFixed(2)}%`,
      },
      {
        field: 'avgEncounterCompletionVelocity',
        headerName: 'Average encounter completion velocity',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'numberOfAnnotators',
        headerName: 'No. of annotators',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'averageEncounterCompletionVelocityPerAnnotator',
        headerName: 'Average encounter completion velocity per annotator',
        flex: 0.6,
        minWidth: 200,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'irrOverLastDay',
        headerName: 'Inter-rater reliability over last day',
        flex: 0.6,
        minWidth: 200,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'irrAverage',
        headerName: 'Inter-rater reliability average',
        flex: 0.6,
        minWidth: 200,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
    ],
    [formatMessage, theme]
  );

  return (
    <Box>
      <DataGridLayout id="metrics-summary-data-grid">
        <DataGrid
          id="metrics-summary-data-grid"
          mode="client"
          pagination
          loadOnFetch
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          paginationModel={{ page: summaryTablePage, pageSize: summaryTablePageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSearchParamSync
          preventUrlReadWhenSyncDisabled
          getRowId={(row) => row.id}
          columns={columns}
          slots={{ toolbar: DatagridEmailExportToolbar }}
          useFetchDataGridData={useMetricSummayDatagridFetch}
          initialState={{
            pinnedColumns: {
              left: ['srNo', 'projectName'],
            },
          }}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: '1.2',
              textAlign: 'center',
            },

            '& .MuiDataGrid-columnHeader': {
              height: 'auto !important',
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
